import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Trophy, Plus, Users, Play, Crown, Clock, ChevronRight, Swords, Search, X, Check, Share2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL;

function BracketView({ tournament }) {
  const rounds = tournament.rounds || [];
  const players = Object.fromEntries((tournament.players || []).map(p => [p.user_id, p.name]));
  const getName = (id) => id ? (players[id] || "TBD") : "—";
  const roundLabels = rounds.length <= 1 ? ["Final"] : rounds.map((_, i) => {
    if (i === rounds.length - 1) return "Final";
    if (i === rounds.length - 2) return "Semi-Finals";
    return `Round ${i + 1}`;
  });

  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex gap-6 min-w-max">
        {rounds.map((rnd, rIdx) => (
          <div key={rIdx} className="flex flex-col gap-4" style={{ justifyContent: "center" }}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-center text-[hsl(var(--muted-foreground))]">{roundLabels[rIdx]}</p>
            {rnd.matches.map((match) => (
              <div key={match.id} className={`rounded-lg border overflow-hidden w-52 ${match.winner_id ? 'border-[hsl(var(--border))]' : 'border-[hsl(var(--primary))]/30'}`} style={{ background: "hsl(var(--card))" }}>
                <MatchSlot name={getName(match.player1_id)} score={match.player1_score} isWinner={match.winner_id === match.player1_id} submitted={match.player1_submitted} />
                <div className="h-px bg-[hsl(var(--border))]" />
                <MatchSlot name={getName(match.player2_id)} score={match.player2_score} isWinner={match.winner_id === match.player2_id} submitted={match.player2_submitted} />
                {match.is_sudden_death && !match.winner_id && (
                  <div className="text-[10px] text-center py-1 bg-red-500/10 text-red-400 font-bold">SUDDEN DEATH</div>
                )}
              </div>
            ))}
          </div>
        ))}
        {tournament.champion_name && (
          <div className="flex flex-col justify-center">
            <div className="rounded-lg border-2 border-[hsl(var(--primary))] p-4 text-center w-52 bg-[hsl(var(--primary))]/10">
              <Crown size={28} className="mx-auto text-[hsl(var(--primary))] mb-2" />
              <p className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Champion</p>
              <p className="text-lg font-bold text-[hsl(var(--primary))]">{tournament.champion_name}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MatchSlot({ name, score, isWinner, submitted }) {
  return (
    <div className={`flex items-center justify-between px-3 py-2 ${isWinner ? 'bg-[hsl(var(--primary))]/10' : ''}`}>
      <div className="flex items-center gap-2 min-w-0">
        {isWinner && <Crown size={12} className="text-[hsl(var(--primary))] shrink-0" />}
        <span className={`text-sm truncate ${isWinner ? 'font-bold text-[hsl(var(--primary))]' : name === '—' ? 'text-[hsl(var(--muted-foreground))] italic' : ''}`}>{name}</span>
      </div>
      {score !== null && score !== undefined && (
        <span className={`text-sm font-mono font-bold ${isWinner ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--muted-foreground))]'}`}>{score}</span>
      )}
      {!submitted && score === null && name !== '—' && name !== 'TBD' && (
        <Clock size={12} className="text-[hsl(var(--muted-foreground))]" />
      )}
    </div>
  );
}

export function TournamentSection({ currentUser }) {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [cocktails, setCocktails] = useState([]);
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);

  // Create form
  const [name, setName] = useState("");
  const [selectedCocktailIds, setSelectedCocktailIds] = useState([]);
  const [cocktailSearch, setCocktailSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [tRes, cRes, dRes] = await Promise.all([
        axios.get(`${API}/tournaments`),
        axios.get(`${API}/cocktails`),
        axios.get(`${API}/decks`),
      ]);
      const uid = currentUser?.user_id;
      const all = tRes.data.filter(t => t.status === "open" || t.status === "in_progress");
      all.sort((a, b) => {
        const aIn = a.players?.some(p => p.user_id === uid) ? 0 : 1;
        const bIn = b.players?.some(p => p.user_id === uid) ? 0 : 1;
        return aIn - bIn;
      });
      setTournaments(all);
      setCocktails(cRes.data);
      setDecks(dRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [currentUser]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-join from share link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const joinId = params.get("join");
    if (joinId && currentUser) {
      axios.post(`${API}/tournaments/${joinId}/join`).then(() => fetchData()).catch(() => {});
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [currentUser, fetchData]);

  const cocktailMap = useMemo(() => Object.fromEntries(cocktails.map(c => [c.id, c])), [cocktails]);

  const categoryDecks = useMemo(() => {
    const groups = {};
    cocktails.forEach(c => { const cat = c.category || "Uncategorized"; if (!groups[cat]) groups[cat] = []; groups[cat].push(c); });
    return Object.entries(groups).map(([n, items]) => ({ name: n, cocktail_ids: items.map(c => c.id), count: items.length }));
  }, [cocktails]);

  const customDecks = useMemo(() => decks.map(d => ({ name: d.name, cocktail_ids: d.cocktail_ids || [], count: (d.cocktail_ids || []).length })).filter(d => d.count > 0), [decks]);
  const allDecks = [...customDecks, ...categoryDecks];

  const filteredCocktails = useMemo(() => {
    if (!cocktailSearch.trim()) return [];
    const q = cocktailSearch.toLowerCase();
    return cocktails.filter(c => c.name.toLowerCase().includes(q)).slice(0, 15);
  }, [cocktails, cocktailSearch]);

  const toggleCocktail = (id) => setSelectedCocktailIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const addDeck = (deck) => setSelectedCocktailIds(prev => { const set = new Set(prev); deck.cocktail_ids.forEach(id => set.add(id)); return [...set]; });

  const handleCreate = async () => {
    if (!name.trim() || selectedCocktailIds.length === 0) return;
    try {
      await axios.post(`${API}/tournaments`, { name: name.trim(), cocktail_ids: selectedCocktailIds, questions_per_match: 10, time_per_question: 0 });
      setShowCreate(false); setName(""); setSelectedCocktailIds([]); setCocktailSearch("");
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleJoin = async (id) => { try { await axios.post(`${API}/tournaments/${id}/join`); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const handleStart = async (id) => { try { await axios.post(`${API}/tournaments/${id}/start`); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const handleDelete = async (id) => { if (!window.confirm("Delete this tournament?")) return; try { await axios.delete(`${API}/tournaments/${id}`); fetchData(); toast.success("Deleted"); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const shareLink = (id) => { navigator.clipboard.writeText(`${APP_URL}/my-dashboard?join=${id}`).then(() => toast.success("Tournament link copied!")); };
  const openBracket = async (id) => { try { const res = await axios.get(`${API}/tournaments/${id}`); setSelectedTournament(res.data); } catch {} };

  const findMyMatch = (t) => {
    if (!currentUser) return null;
    for (const rnd of t.rounds || []) {
      for (const match of rnd.matches) {
        const isP1 = match.player1_id === currentUser.user_id;
        const isP2 = match.player2_id === currentUser.user_id;
        if ((isP1 || isP2) && !match.winner_id) {
          const submitted = isP1 ? match.player1_submitted : match.player2_submitted;
          if (!submitted) return { tournament_id: t.id, match_id: match.id };
        }
      }
    }
    return null;
  };

  if (loading) return null;

  return (
    <>
      <div data-testid="tournaments-section">
        <div className="flex items-center justify-between gap-2 mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2 min-w-0">
            <Swords size={20} className="text-[hsl(var(--primary))] shrink-0" />
            <span className="truncate">Tournaments</span>
          </h2>
          <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[hsl(var(--primary))] text-black shrink-0" data-testid="dashboard-create-tournament">
            <Plus size={14} className="mr-1" /> <span className="hidden sm:inline">New Tournament</span><span className="sm:hidden">New</span>
          </Button>
        </div>

        {tournaments.length === 0 ? (
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6 text-center text-[hsl(var(--muted-foreground))]">
              No active tournaments. Create one to challenge your team!
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tournaments.map(t => {
              const isCreator = currentUser?.user_id === t.creator_id;
              const isJoined = t.players?.some(p => p.user_id === currentUser?.user_id);
              const myMatch = t.status === "in_progress" ? findMyMatch(t) : null;

              return (
                <Card key={t.id} className="bg-[hsl(var(--card))] border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/40 transition-all" data-testid={`tournament-${t.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-bold text-sm truncate flex-1">{t.name}</h3>
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full shrink-0 ${
                        t.status === "open" ? "bg-green-500/20 text-green-400" : "bg-[#C36A2F]/20 text-[#C36A2F]"
                      }`}>{t.status === "in_progress" ? "Live" : "Open"}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))] mb-3">
                      <span className="flex items-center gap-1"><Users size={12} /> {t.players?.length}</span>
                      <span>by {t.creator_name}</span>
                      {t.champion_name && <span className="flex items-center gap-1 text-[hsl(var(--primary))]"><Crown size={12} /> {t.champion_name}</span>}
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      {myMatch && (
                        <Button size="sm" onClick={() => navigate(`/tournament/${myMatch.tournament_id}/match/${myMatch.match_id}`)} className="bg-red-500 text-white text-xs h-7 px-2" data-testid={`play-match-btn-${t.id}`}>
                          <Swords size={12} className="mr-1" /> Play
                        </Button>
                      )}
                      {t.status === "open" && !isJoined && (
                        <Button size="sm" variant="outline" onClick={() => handleJoin(t.id)} className="text-xs h-7 px-2" data-testid={`join-btn-${t.id}`}>Join</Button>
                      )}
                      {t.status === "open" && isCreator && t.players?.length >= 2 && (
                        <Button size="sm" onClick={() => handleStart(t.id)} className="bg-[hsl(var(--primary))] text-black text-xs h-7 px-2" data-testid={`start-btn-${t.id}`}>
                          <Play size={12} className="mr-1" /> Start
                        </Button>
                      )}
                      {(t.status === "in_progress" || t.status === "complete") && (
                        <Button size="sm" variant="outline" onClick={() => openBracket(t.id)} className="text-xs h-7 px-2" data-testid={`bracket-btn-${t.id}`}>
                          Bracket
                        </Button>
                      )}
                      {t.status === "open" && isCreator && (
                        <Button size="sm" variant="ghost" onClick={() => shareLink(t.id)} className="text-[hsl(var(--muted-foreground))] h-7 w-7 p-0" data-testid={`share-btn-${t.id}`}>
                          <Share2 size={12} />
                        </Button>
                      )}
                      {isCreator && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(t.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0" data-testid={`delete-btn-${t.id}`}>
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Tournament Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg">
          <DialogHeader><DialogTitle>Create Tournament</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Tournament Name</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Friday Night Showdown" className="bg-[hsl(var(--secondary))]" data-testid="tournament-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Quick Add from Deck</label>
              <div className="flex flex-wrap gap-1.5">
                {allDecks.map((d, i) => (
                  <button key={i} onClick={() => addDeck(d)} className="px-2.5 py-1 rounded-full text-xs font-medium border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] hover:border-[hsl(var(--primary))]/50 transition-colors" data-testid={`deck-shortcut-${i}`}>
                    {d.name} ({d.count})
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Search & Add Cocktails</label>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] pointer-events-none" />
                <Input value={cocktailSearch} onChange={e => setCocktailSearch(e.target.value)} placeholder="Search cocktails..." className="bg-[hsl(var(--secondary))] pl-8" data-testid="cocktail-search-input" />
              </div>
              {filteredCocktails.length > 0 && (
                <div className="mt-1 max-h-36 overflow-y-auto rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                  {filteredCocktails.map(c => {
                    const sel = selectedCocktailIds.includes(c.id);
                    return (
                      <button key={c.id} onClick={() => toggleCocktail(c.id)} className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[hsl(var(--accent))] transition-colors ${sel ? 'text-[hsl(var(--primary))]' : ''}`} data-testid={`cocktail-search-result-${c.id}`}>
                        <span>{c.name}</span>
                        {sel && <Check size={14} className="text-[hsl(var(--primary))]" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Selected ({selectedCocktailIds.length})</label>
              {selectedCocktailIds.length === 0 ? (
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Add cocktails from a deck or search above</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                  {selectedCocktailIds.map(id => { const c = cocktailMap[id]; return c ? (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/20">
                      {c.name} <button onClick={() => toggleCocktail(id)} className="hover:text-red-400" aria-label={`Remove ${c.name}`}><X size={12} /></button>
                    </span>
                  ) : null; })}
                </div>
              )}
            </div>
            <Button onClick={handleCreate} disabled={!name.trim() || selectedCocktailIds.length === 0} className="w-full bg-[hsl(var(--primary))] text-black" data-testid="create-tournament-submit">
              Create Tournament ({selectedCocktailIds.length} cocktails)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bracket View Dialog */}
      <Dialog open={!!selectedTournament} onOpenChange={() => setSelectedTournament(null)}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy size={20} className="text-[hsl(var(--primary))]" />
              {selectedTournament?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedTournament && <BracketView tournament={selectedTournament} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
